export type tMenuSide = 'left' | 'right';

/**
 * Where the menu opens, in the Dropdown's own props: beside the arrow
 * (`horizontal`), or below it when neither side has room — aligned to the
 * arrow's left edge, or to its right edge with `end`.
 *
 * "Below" needs `vertical: 'bottom'`: daisyUI gives the menu `top: 100%` only
 * with `dropdown-bottom`. Without it the menu sits level with the arrow and
 * covers it, so a second click on the arrow lands on "Responder".
 */
export type tMessageMenuPlacement = {
  horizontal?: tMenuSide;
  vertical?: 'bottom';
  end?: boolean;
};

type tHorizontalBounds = Pick<DOMRect, 'left' | 'right'>;

/**
 * The conversation column. It scrolls with `overflow-hidden` on the x axis, so
 * whatever part of the menu crosses its edge is cut off — behind the
 * attributes sidebar on the right, behind the chat list on the left.
 */
export const MESSAGE_MENU_BOUNDS_SELECTOR =
  '.MessageContainer__Chat, .MessageContainer__ChatDark';

/** The menu's `w-52`, in px. Change both together. */
export const MESSAGE_MENU_WIDTH_PX = 208;

/**
 * BUG-1156: where the menu opens, so that it stays inside the column.
 *
 * The arrow sits at the top right of every bubble since the WhatsApp-style
 * bubbles (514a0948e), so a rule keyed on the message (own / long / quoted)
 * cannot know where it is: an own message hugs the right edge, a short
 * received one the left edge, and a long received one can sit mid-column.
 *
 * Right of the arrow first, as the menu has always opened, then left; each
 * only when the menu fits there. A narrow column (a 1024 px laptop with the
 * chat list and the attributes sidebar open leaves ~416 px) can have less
 * than the menu's width on both sides; the menu then opens below the arrow,
 * which only needs its width counted from one of the arrow's edges — running
 * right again first.
 */
export function pickMessageMenuPlacement(
  toggle: tHorizontalBounds,
  bounds: tHorizontalBounds,
): tMessageMenuPlacement {
  const roomLeft = toggle.left - bounds.left;
  const roomRight = bounds.right - toggle.right;

  if (roomRight >= MESSAGE_MENU_WIDTH_PX) return { horizontal: 'right' };
  if (roomLeft >= MESSAGE_MENU_WIDTH_PX) return { horizontal: 'left' };

  // Below: aligned to the arrow's left edge the menu runs right, aligned to
  // its right edge (`end`) it runs left.
  const roomFromLeftEdge = bounds.right - toggle.left;
  const roomFromRightEdge = toggle.right - bounds.left;

  return {
    vertical: 'bottom',
    end:
      roomFromLeftEdge < MESSAGE_MENU_WIDTH_PX &&
      roomFromRightEdge > roomFromLeftEdge,
  };
}

/**
 * Bounds the menu must stay inside: the conversation column when the arrow is
 * in one, the viewport otherwise (a detached node, a preview page).
 *
 * The column's inner box, not its border box: a classic scrollbar (Windows,
 * ~15 px) sits inside the border box and hides whatever is under it.
 */
export function getMessageMenuBounds(toggle: Element): tHorizontalBounds {
  const column = toggle.closest(MESSAGE_MENU_BOUNDS_SELECTOR);
  if (!column) return { left: 0, right: window.innerWidth };

  const left = column.getBoundingClientRect().left + column.clientLeft;

  return { left, right: left + column.clientWidth };
}
